/* eslint-disable default-case */
import produce from 'immer';

import {
  types,
  MediaActionTypes,
  MediaState,
  CreateMediaParams,
  UploadImageError,
  UploadImageSuccess,
  UploadImagePending,
  UpdateMediaParams,
  GetMediaParams,
  GetMediaSuccess,
  ConfigurationParams,
  AttractMediaType,
  Environment,
  SetEvent,
  SetVenue,
  SetGenre,
  DeleteAssociatedMediaRequest
} from './types';

import { MediaTypes } from 'views/MediaCenterView/types';

// Actions
export const actions = {
  createMedia: (payload: CreateMediaParams): MediaActionTypes => ({
    type: types.CREATE_MEDIA.REQUEST,
    payload
  }),
  updateMedia: (payload: UpdateMediaParams): MediaActionTypes => ({
    type: types.UPDATE_MEDIA.REQUEST,
    payload
  }),
  getMedia: (payload: GetMediaParams): MediaActionTypes => ({
    type: types.GET_MEDIA.REQUEST,
    payload
  }),
  deleteMedia: (payload: string): MediaActionTypes => ({
    type: types.DELETE_MEDIA.REQUEST,
    payload
  }),
  deleteAssociatedMedia: (payload: DeleteAssociatedMediaRequest['payload']): MediaActionTypes => ({
    type: types.DELETE_ASSOCIATED_MEDIA.REQUEST,
    payload
  }),
  resetAction: () => ({ type: types.RESET_ACTION }),
  resetMedia: () => ({ type: types.RESET_MEDIA }),
  setUploadImageError: (payload: UploadImageError['payload']): MediaActionTypes => ({
    type: types.CREATE_MEDIA.ERROR,
    payload
  }),
  setConfiguration: (payload: ConfigurationParams['payload']): MediaActionTypes => ({
    type: types.SET_CONFIGURATION,
    payload
  }),
  setAttractMediaType: (payload: AttractMediaType['payload']): MediaActionTypes => ({
    type: types.SET_ATTRACT_MEDIA_TYPE,
    payload
  }),
  setEnvironment: (payload: Environment['payload']): MediaActionTypes => ({
    type: types.SET_ENVIRONMENT,
    payload
  }),
  setEvent: (payload: SetEvent['payload']): MediaActionTypes => ({
    type: types.SET_EVENT,
    payload
  }),
  setVenue: (payload: SetVenue['payload']): MediaActionTypes => ({
    type: types.SET_VENUE,
    payload
  }),
  setGenre: (payload: SetGenre['payload']): MediaActionTypes => ({
    type: types.SET_GENRE,
    payload
  })
};

// Initial State
export const initialState: MediaState = {
  action: {
    response: null,
    successful: false,
    pending: false,
    error: null
  },
  configuration: null,
  environment: null,
  attract: {
    mediaType: null,
    media: []
  },
  otherImages: {
    inactive: [],
    splash: [],
    logo: [],
    logoLarge: [],
    background: [],
    promoCode: []
  },
  event: {
    selected: '',
    media: []
  },
  venue: {
    selected: '',
    media: []
  },
  genre: {
    selected: '',
    media: []
  }
};

// Reducer
export const reducer = (state: MediaState = initialState, action: MediaActionTypes): MediaState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case types.RESET_ACTION:
        draft.action = initialState.action;
        break;
      case types.RESET_MEDIA:
        draft.action = initialState.action;
        draft.configuration = initialState.configuration;
        draft.event = initialState.event;
        draft.attract = initialState.attract;
        draft.otherImages = initialState.otherImages;
        draft.venue = initialState.venue;
        draft.genre = initialState.genre;
        break;
      case types.CREATE_MEDIA.SUCCESS:
      case types.UPDATE_MEDIA.SUCCESS:
        draft.action.successful = true;
        draft.action.response = (action as UploadImageSuccess).payload;
        break;
      case types.DELETE_MEDIA.SUCCESS:
      case types.DELETE_ASSOCIATED_MEDIA.SUCCESS:
        draft.action.successful = true;
        break;
      case types.CREATE_MEDIA.PENDING:
      case types.UPDATE_MEDIA.PENDING:
      case types.GET_MEDIA.PENDING:
      case types.DELETE_MEDIA.PENDING:
      case types.DELETE_ASSOCIATED_MEDIA.PENDING:
        draft.action.pending = (action as UploadImagePending).payload;
        break;
      case types.CREATE_MEDIA.ERROR:
      case types.UPDATE_MEDIA.ERROR:
      case types.DELETE_ASSOCIATED_MEDIA.ERROR:
        draft.action.error = (action as UploadImageError).payload;
        break;
      case types.SET_CONFIGURATION:
        draft.configuration = (action as ConfigurationParams).payload;
        break;
      case types.SET_ATTRACT_MEDIA_TYPE:
        draft.attract.mediaType = (action as AttractMediaType).payload;
        break;
      case types.SET_ENVIRONMENT:
        draft.environment = (action as Environment).payload;
        break;
      case types.SET_EVENT:
        draft.event.selected = (action as SetEvent).payload;
        break;
      case types.SET_VENUE:
        draft.venue.selected = (action as SetVenue).payload;
        break;
      case types.SET_GENRE:
        draft.genre.selected = (action as SetGenre).payload;
        break;
      case types.GET_MEDIA.SUCCESS:
        const imageResponse = (action as GetMediaSuccess).payload;
        switch (imageResponse.type) {
          case MediaTypes.EVENT:
            draft.event.media = imageResponse.event;
            break;
          case MediaTypes.VENUE:
            draft.venue.media = imageResponse.venue;
            break;
          case MediaTypes.GENRE:
            draft.genre.media = imageResponse.genre;
            break;
          case MediaTypes.ATTRACT_IMAGE:
            draft.attract.media = imageResponse[MediaTypes.ATTRACT_IMAGE];
            break;
          case MediaTypes.ATTRACT_VIDEO:
            draft.attract.media = imageResponse[MediaTypes.ATTRACT_VIDEO];
            break;
          case MediaTypes.INACTIVE_IMAGE:
            draft.otherImages.inactive = imageResponse[MediaTypes.INACTIVE_IMAGE];
            break;
          case MediaTypes.SPLASH_IMAGE:
            draft.otherImages.splash = imageResponse[MediaTypes.SPLASH_IMAGE];
            break;
          case MediaTypes.PROMO_CODE_IMAGE:
            draft.otherImages.promoCode = imageResponse[MediaTypes.PROMO_CODE_IMAGE];
            break;
          case MediaTypes.LOGO_IMAGE:
            draft.otherImages.logo = imageResponse[MediaTypes.LOGO_IMAGE];
            break;
          case MediaTypes.LOGO_IMAGE_LARGE:
            draft.otherImages.logoLarge = imageResponse[MediaTypes.LOGO_IMAGE_LARGE];
            break;
          case MediaTypes.BACKGROUND_IMAGE:
            draft.otherImages.background = imageResponse[MediaTypes.BACKGROUND_IMAGE];
            break;
        }
        break;
    }
  });
};
